/**
 * Цена состава блюда (конструктор): «входит в цену» и бесплатный лимит группы.
 * Правила повторяют облако — App\Helper\ModifierPricing (тесты с теми же случаями).
 *
 *  - у варианта base_price_included: штуки в количестве default_quantity входят в цену блюда;
 *  - у группы free_quantity: столько штук группы бесплатно на одну порцию блюда.
 *
 * Порядок: 1) «входит в цену» — первые default × порции штук товара; 2) остаток делит бесплатный
 * лимит: бесплатны САМЫЕ ДОРОГИЕ штуки, платны самые дешёвые (при равной цене — выбранные раньше).
 * «Любые N бесплатно» гость и так использует на дорогое, поэтому итог зависит только от набора,
 * а не от порядка нажатий: любой кассир и любой канал назовут гостю одну сумму. Варианты по
 * нулевой цене лимит не тратят; 3) остальное платно.
 * Строка чека хранит целое количество и цену за штуку, поэтому вариант с бесплатной частью
 * раскладывается на строки: платная по цене и входящие/бесплатные по нулю.
 *
 * Модуль чистый (без импортов): один и тот же файл лежит в кассе (src/custom/modifierPricing.js)
 * и в кабинете колл-центра (src/helpers/modifierPricing.js) — правишь в обоих.
 */

export const MODIFIER_PRICE_KIND = {
	REGULAR: 'regular',
	INCLUDED: 'included',
	FREE: 'free',
}

function wholeCount(value) {
	const n = Math.floor(Number(value))
	return Number.isFinite(n) && n > 0 ? n : 0
}

function priceOf(value) {
	const n = Number(value)
	return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : 0
}

function isOn(value) {
	return value === true || value === 1 || value === '1' || value === 't' || value === 'true' || value === 'TRUE'
}

/**
 * @param {{ free_quantity?: number }} group
 * @param {Array<{ key: any, variant?: any, quantity: number, price: number, default_quantity?: number, base_price_included?: boolean }>} picks
 *   key уникален, variant — товар (по нему копится «входит в цену»), по умолчанию = key
 * @param {number} portions сколько порций блюда: лимит и «входит в цену» умножаются на него
 * @returns {{ items: Map<any, object>, total: number }}
 */
export function allocateModifierPricing(group, picks, portions = 1) {
	const scale = Math.max(1, wholeCount(portions))
	let freePool = wholeCount(group?.free_quantity) * scale
	const items = new Map()
	const candidates = []
	const includedUsed = new Map()

	;(picks || []).forEach((pick, order) => {
		const quantity = wholeCount(pick.quantity)
		if (quantity === 0) return

		const price = priceOf(pick.price)
		const key = pick.key ?? order
		const variant = pick.variant ?? key
		// «Входит в цену» копится по товару: вариант, выбранный в два захода, получает свои штуки один раз
		const includedLimit = isOn(pick.base_price_included) ? wholeCount(pick.default_quantity) * scale : 0
		const included = Math.min(quantity, Math.max(0, includedLimit - (includedUsed.get(variant) || 0)))
		includedUsed.set(variant, (includedUsed.get(variant) || 0) + included)
		const item = { quantity, included, free: 0, paid: quantity - included, price }
		items.set(key, item)

		// По нулевой цене платить нечего — лимит такие штуки не тратят
		if (price > 0 && item.paid > 0) candidates.push({ key, price, order })
	})

	// Бесплатный лимит достаётся самым дорогим штукам; при равной цене — выбранным раньше
	candidates.sort((a, b) => (b.price - a.price) || (a.order - b.order))

	for (const candidate of candidates) {
		if (freePool <= 0) break
		const item = items.get(candidate.key)
		const take = Math.min(freePool, item.paid)
		item.free = take
		item.paid -= take
		freePool -= take
	}

	let total = 0
	items.forEach((item) => {
		item.charge = Math.round(item.paid * item.price * 100) / 100
		item.lines = linesOf(item)
		total += item.charge
	})

	return { items, total: Math.round(total * 100) / 100 }
}

function linesOf(item) {
	// Вариант по нулевой цене делить не на что: одна строка на всё количество
	if (item.price <= 0) {
		return [{ kind: MODIFIER_PRICE_KIND.REGULAR, quantity: item.quantity, price: 0 }]
	}

	const lines = []
	if (item.paid > 0) lines.push({ kind: MODIFIER_PRICE_KIND.REGULAR, quantity: item.paid, price: item.price })
	if (item.included > 0) lines.push({ kind: MODIFIER_PRICE_KIND.INCLUDED, quantity: item.included, price: 0 })
	if (item.free > 0) lines.push({ kind: MODIFIER_PRICE_KIND.FREE, quantity: item.free, price: 0 })
	return lines
}

/** Правила цены у группы включены: есть бесплатный лимит или «входит в цену» со штуками по умолчанию */
function groupHasPricingRules(lines) {
	return lines.some((line) => (
		wholeCount(line.group_free_quantity) > 0
		|| (isOn(line.base_price_included) && wholeCount(line.default_quantity) > 0)
	))
}

/** Модификатор или компонент конструктора: по типу товара либо по связи с блюдом */
export function isModifierLine(line) {
	if (!line) return false
	const typeValue = line.product_type_value != null ? String(line.product_type_value).trim().toLowerCase() : null
	if (typeValue === 'modifier') return true
	if (typeValue == null && Number(line.product_type_id ?? line.in_product) === 3) return true
	if (Number(line.modifier_parent_id) > 0) return true
	if (line.parent_cheque_product_id != null && line.parent_cheque_product_id !== '') return true
	return line.parent_transaction_id != null && String(line.parent_transaction_id).trim() !== ''
}

/** Строку состава можно пересобрать: она в этой сессии ещё не ушла и несёт правила цены из меню */
function isRepriceableLine(line, isEditable) {
	return isModifierLine(line)
		&& isEditable(line)
		&& line.modifier_price != null
		&& line.modifier_parent_id != null
}

function lineKind(line) {
	return line.price_kind || MODIFIER_PRICE_KIND.REGULAR
}

/**
 * Пересобирает строки состава у новых позиций чека по правилам цены группы.
 *
 * Работает только с группами, где правила включены, — остальные не трогает, поэтому обычные
 * модификаторы (и цена, которую кассир поправил руками) остаются как были. Строки, которые уже
 * нельзя менять (isEditable = false: ушли на кухню, сохранены), тоже не меняются: лимит считается
 * по строкам, добавленным до отправки. Идемпотентна: у строки остаётся её transaction_id, лишние
 * строки появляются и исчезают по необходимости.
 *
 * Строки одного товара, выбранные в разные заходы, остаются отдельными строками в порядке чека
 * (BBQ, сырный, ещё BBQ — три строки): при равной цене бесплатной остаётся выбранная раньше.
 * Строка, разбитая на платную и бесплатную части, помнит исходную (origin_tx) и при следующем
 * пересчёте собирается обратно.
 *
 * @param {Array} products строки чека
 * @param {{ isEditable: (line: object) => boolean, createTransactionId: () => string }} deps
 * @returns {{ products: Array, changed: boolean }}
 */
export function repriceModifierLines(products, { isEditable, createTransactionId }) {
	if (!Array.isArray(products) || products.length === 0) return { products, changed: false }

	const byParent = new Map()
	products.forEach((line) => {
		if (!isRepriceableLine(line, isEditable)) return
		const parentKey = line.parent_transaction_id
		if (parentKey == null || String(parentKey).trim() === '') return
		if (!byParent.has(parentKey)) byParent.set(parentKey, new Map())
		const groups = byParent.get(parentKey)
		if (!groups.has(line.modifier_parent_id)) groups.set(line.modifier_parent_id, [])
		groups.get(line.modifier_parent_id).push(line)
	})

	// rebuilt: первая старая строка варианта → новые строки; остальные старые строки варианта убираются
	const rebuilt = new Map()
	const dropped = new Set()
	let changed = false

	byParent.forEach((groups, parentKey) => {
		const parent = products.find((line) => line.transaction_id === parentKey && !isModifierLine(line))
		const portions = Math.max(1, wholeCount(parent?.quantity))

		groups.forEach((groupLines) => {
			if (!groupHasPricingRules(groupLines)) return

			// Исходные строки в порядке чека: части одной строки (платная/бесплатная) собираем обратно
			const origins = []
			const linesByOrigin = new Map()
			groupLines.forEach((line) => {
				const originId = line.origin_tx || line.transaction_id
				if (!linesByOrigin.has(originId)) {
					linesByOrigin.set(originId, [])
					origins.push(originId)
				}
				linesByOrigin.get(originId).push(line)
			})

			const picks = origins.map((originId) => {
				const first = linesByOrigin.get(originId)[0]
				return {
					key: originId,
					variant: first.product_id,
					quantity: linesByOrigin.get(originId).reduce((sum, line) => sum + (Number(line.quantity) || 0), 0),
					price: first.modifier_price,
					default_quantity: first.default_quantity,
					base_price_included: first.base_price_included,
				}
			})

			const allocation = allocateModifierPricing(
				{ free_quantity: groupLines[0].group_free_quantity },
				picks,
				portions
			)

			origins.forEach((originId) => {
				const oldLines = linesByOrigin.get(originId)
				const item = allocation.items.get(originId)
				const wanted = item ? item.lines : []
				const keepSelected = oldLines.some((line) => line.selected)
				const spare = [...oldLines]
				const next = wanted.map((want, i) => {
					const reuseIndex = spare.findIndex((line) => lineKind(line) === want.kind)
					const base = reuseIndex >= 0 ? spare.splice(reuseIndex, 1)[0] : null
					const line = base || {
						...oldLines[0],
						id: undefined,
						transaction_id: createTransactionId(),
					}
					line.origin_tx = originId
					line.quantity = want.quantity
					line.sale_price = want.price
					line.total_price = want.price * want.quantity
					line.price_kind = want.kind
					line.selected = keepSelected && i === 0
					return line
				})

				const unchanged = next.length === oldLines.length
					&& next.every((line, i) => line === oldLines[i])
				if (!unchanged) changed = true

				rebuilt.set(oldLines[0], next)
				oldLines.slice(1).forEach((line) => dropped.add(line))
			})
		})
	})

	if (rebuilt.size === 0) return { products, changed: false }

	const result = []
	products.forEach((line) => {
		if (rebuilt.has(line)) {
			result.push(...rebuilt.get(line))
			return
		}
		if (dropped.has(line)) return
		result.push(line)
	})

	return { products: result, changed: changed || result.length !== products.length }
}
